import React from 'react';
import { MapPin } from 'lucide-react';
import type { Trader, TraderFeatures } from '../services/traderService';
// MockupBadge removed - no longer showing demo badges

interface TraderCardProps {
  trader: Trader & { trader_features?: TraderFeatures };
  onClick?: () => void;
}

export default function TraderCard({ trader, onClick }: TraderCardProps) {
  // Collect enabled features with proper checks
  const enabledFeatures = React.useMemo(() => {
    if (!trader.trader_features) return [];

    const features = [
      trader.trader_features.feature1_enabled && trader.trader_features.feature1_image && {
        title: trader.trader_features.feature1_title || 'Feature 1',
        description: trader.trader_features.feature1_description || '',
        image: trader.trader_features.feature1_image
      },
      trader.trader_features.feature2_enabled && trader.trader_features.feature2_image && {
        title: trader.trader_features.feature2_title || 'Feature 2',
        description: trader.trader_features.feature2_description || '',
        image: trader.trader_features.feature2_image
      },
      trader.trader_features.feature3_enabled && trader.trader_features.feature3_image && {
        title: trader.trader_features.feature3_title || 'Feature 3',
        description: trader.trader_features.feature3_description || '',
        image: trader.trader_features.feature3_image
      }
    ].filter(Boolean) as Array<{ title: string; description: string; image: string }>;

    return features;
  }, [trader.trader_features]);

  // Log trader and features for debugging
  console.log('Trader in TraderCard:', trader);
  console.log('Trader Features:', trader.trader_features);
  console.log('Enabled Features:', enabledFeatures);

  // More detailed logging for feature debugging
  console.log('Trader Features Debugging:', {
    feature1_enabled: trader.trader_features?.feature1_enabled,
    feature1_image: trader.trader_features?.feature1_image,
    feature2_enabled: trader.trader_features?.feature2_enabled,
    feature2_image: trader.trader_features?.feature2_image,
    feature3_enabled: trader.trader_features?.feature3_enabled,
    feature3_image: trader.trader_features?.feature3_image,
  });

  return (
    <div 
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
      onClick={onClick}
    >
      <div className="relative h-40 sm:h-48">
        <img
          src={trader.avatar_url || '/default-store.jpg'}
          alt={trader.store_name}
          className="w-full h-full object-cover rounded-t-xl"
        />
        {/* Demo badge removed */}
        {trader.verified && (
          <div className="absolute top-3 right-3 bg-blue-600 text-white px-2.5 py-1 rounded-full text-xs sm:text-sm">
            Verified
          </div>
        )}
      </div>
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 line-clamp-2">
            {trader.store_name}
          </h3>

        </div>
        <p className="text-gray-600 text-sm sm:text-base mb-2 line-clamp-1">{trader.owner_name}</p>
        <div className="flex items-center text-gray-500 text-xs sm:text-sm">
          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
          <span className="line-clamp-1">{trader.location}</span>
        </div>
        {trader.phone_number && (
          <div className="mt-1 text-xs text-gray-500">
            📞 {trader.phone_number}
          </div>
        )}
        {trader.website && (
          <div className="mt-1 text-xs text-gray-500 truncate">
            🌐 {trader.website}
          </div>
        )}
        <div className="mt-2 text-xs text-gray-500">
          {trader.reviews} reviews
        </div>
        {trader.description && (
          <p className="mt-2 text-xs text-gray-600 line-clamp-2">
            {trader.description}
          </p>
        )}
        
        {/* Render enabled features */}
        {enabledFeatures.length > 0 && (
          <div className="mt-3 border-t pt-2">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">
              Featured Offerings
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {enabledFeatures.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex flex-col items-center text-center"
                >
                  <img 
                    src={feature.image} 
                    alt={feature.title || `Feature ${index + 1}`}
                    className="w-8 h-8 object-contain mb-1"
                    onError={(e) => {
                      // Fallback if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  <span className="text-[10px] text-gray-600 line-clamp-1">
                    {feature.title}
                  </span>
                  <span className="text-[10px] text-gray-600 line-clamp-1">
                    {feature.description}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}